import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  OnInit,
  ChangeDetectorRef,
  DestroyRef,
} from '@angular/core';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
} from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationService, MessageService } from 'primeng/api';

import { SkeletonModule } from 'primeng/skeleton';
import { SelectModule } from 'primeng/select';

import {
  EfetivoService,
  FuncaoService,
  PostoService,
} from '@core/services/domain-services';

import { ListaEfetivosComponent } from './lista-efetivos/lista-efetivos.component';
import { Pagination } from '@shared/api/models/pagination.model';
import { EnumService } from '@shared/api/services/enum.service';
import { CategoriaService } from '@shared/api/services/categoria.service';
import { CriarEfetivoComponent } from './criar-efetivo/criar-efetivo.component';
import { EditarEfetivoComponent } from './editar-efetivo/editar-efetivo.component';
import { FichaEfetivoDrawerComponent } from './ficha-efetivo-drawer/ficha-efetivo-drawer.component';

import {
  GeneroEnum,
  EstadoEfetivoEnum,
} from '@core/models/models';

import { EfetivoEstatisticas } from '@shared/api/services/estatisticas/estatisticas-info.service';

export type TabType =
  | 'todos'
  | 'Ativo'
  | 'Inativo'
  | 'Afastado'
  | 'Suspensos'
  | 'Ferias'
  | 'De_Folga';

@Component({
  selector: 'app-efetivos',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SkeletonModule,
    SelectModule,
    ListaEfetivosComponent,
    CriarEfetivoComponent,
    EditarEfetivoComponent,
    FichaEfetivoDrawerComponent,
  ],
  templateUrl: './efetivos.component.html',
  styleUrl: './efetivos.component.scss',
})
export class EfetivosComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly efetivoSvc = inject(EfetivoService);
  private readonly postoSvc = inject(PostoService);
  private readonly msgService = inject(MessageService);
  private readonly confirmSvc = inject(ConfirmationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly enumService = inject(EnumService);
  private readonly funcaoService = inject(FuncaoService);
  private readonly categoriaService = inject(CategoriaService);

  // ESTATÍSTICAS
  private readonly efetivoEstatisticasInfo = inject(EfetivoEstatisticas);

  // DADOS DAS ESTATÍSTICAS
  totalColaboradores = 0;
  ativos = 0;
  inativos = 0;
  afastados = 0;
  suspensos = 0;
  ferias = 0;
  deFolga = 0;

  activeTab: TabType = 'todos';

  filtrosPainelAberto = false;
  mostrarDrawer = false;
  mostrarDrawerEditar = false;
  efetivoIdParaEditar: number | null = null;

  fichaDrawerVisible = false;
  fichaEfetivoId: number | null = null;
  fichaPendingAction: 'suspender' | 'afastar' | null = null;
  reabrirFichaAposEditar = false;

  filtrosForm!: FormGroup;

  funcoes: any[] = [];
  categorias: any[] = [];
  generos: any[] = [];
  postos: any[] = [];

  estadoOptions = [
    {
      label: 'Ativo',
      value: EstadoEfetivoEnum.ATIVO,
    },
    {
      label: 'Inativo',
      value: EstadoEfetivoEnum.INATIVO,
    },
    {
      label: 'Afastado',
      value: EstadoEfetivoEnum.AFASTADO,
    },
    {
      label: 'Suspenso',
      value: EstadoEfetivoEnum.SUSPENSO,
    },
    {
      label: 'De Folga',
      value: EstadoEfetivoEnum.DE_FOLGA,
    },
    {
      label: 'Férias',
      value: EstadoEfetivoEnum.FERIAS,
    },
  ];

  // ── KPIs ────────────────────────────────────────────────

  indicators = [
    {
      key: 'total',
      label: 'Total de Efetivos',
      value: '0',
      icon: 'ri-team-line',
      theme: 'info',
    },
    {
      key: 'postos',
      label: 'Postos Activos',
      value: '0',
      icon: 'ri-map-pin-2-line',
      theme: 'success',
    },
    {
      key: 'ativos',
      label: 'Efetivos Activos',
      value: '0',
      icon: 'ri-user-follow-line',
      theme: 'info',
    },
    {
      key: 'inativos',
      label: 'Efetivos Inactivos',
      value: '0',
      icon: 'ri-user-unfollow-line',
      theme: 'warning',
    },
    {
      key: 'escalas',
      label: 'Escalas Hoje',
      value: '0',
      icon: 'ri-calendar-check-line',
      theme: 'secondary',
    },
  ];

  loadingKpis = true;

  // ── LIST DATA ───────────────────────────────────────────

  pagination: Pagination<any> = new Pagination<any>();

  fotoPreviewMap: Record<number, string | ArrayBuffer | null> = {};

  skeletonLoading = false;

  totalCount = 0;

  // CONTAGENS DOS FILTROS
  countPorEstado: Record<string, number> = {
    todos: 0,
    Ativo: 0,
    Inativo: 0,
    Afastado: 0,
    Suspensos: 0,
    Ferias: 0,
    De_Folga: 0,
  };

  ngOnInit(): void {
    this.filtrosForm = this.fb.group({
      nome: [''],
      numBI: [''],
      genero: [null],
      EstadoEfetivo: [null],
      IdFuncao: [''],
      IdCategoria: [''],
      numTelefone: [''],
      CodigoMec: [''],
      dataAdmissaoDe: [''],
      dataAdmissaoAte: [''],
    });

    this.loadKpis();
    this.loadEfetivos();
    this.loadDropdowns();

    // CARREGA OS DADOS DA API DE ESTATÍSTICAS
    this.estatisticasEfetivos();
  }

  // ───────────────────────────────────────────────────────
  // ESTATÍSTICAS DOS EFETIVOS
  // ───────────────────────────────────────────────────────

  private estatisticasEfetivos(): void {
    this.efetivoEstatisticasInfo
      .obterEfetivoEstatisticas()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (estatisticas) => {
          // Dados gerais
          this.totalColaboradores = estatisticas.totalColaboradores;
          this.ativos = estatisticas.ativos;
          this.inativos = estatisticas.inativos;
          this.afastados = estatisticas.afastados;
          this.suspensos = estatisticas.suspensos;
          this.ferias = estatisticas.ferias;
          this.deFolga = estatisticas.deFolga;

          // Dados usados diretamente nos tabs
          this.countPorEstado['todos'] =
            estatisticas.totalColaboradores;

          this.countPorEstado['Ativo'] =
            estatisticas.ativos;

          this.countPorEstado['Inativo'] =
            estatisticas.inativos;

          this.countPorEstado['Afastado'] =
            estatisticas.afastados;

          this.countPorEstado['Suspensos'] =
            estatisticas.suspensos;

          this.countPorEstado['Ferias'] =
            estatisticas.ferias;

          this.countPorEstado['De_Folga'] =
            estatisticas.deFolga;

          // Actualiza o KPI de total
          this.setKpi(
            'total',
            estatisticas.totalColaboradores,
          );

          this.setKpi(
            'ativos',
            estatisticas.ativos,
          );

          this.setKpi(
            'inativos',
            estatisticas.inativos,
          );

          this.cdr.detectChanges();
        },

        error: (err) => {
          console.error(
            'Erro ao carregar estatísticas dos efetivos:',
            err,
          );
        },
      });
  }

  // ───────────────────────────────────────────────────────
  // KPIs
  // ───────────────────────────────────────────────────────

  loadKpis(): void {
    this.loadingKpis = true;

    forkJoin({
      efetivos: this.efetivoSvc.getAll({
        pageSize: 1,
      }),

      efetivosAtivos: this.efetivoSvc.getAll({
        pageSize: 1,
        EstadoEfetivo: 'Ativo',
      }),

      efetivosInativos: this.efetivoSvc.getAll({
        pageSize: 1,
        EstadoEfetivo: 'Inativo',
      }),

      postos: this.postoSvc.getAll({
        pageSize: 1,
      }),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r) => {
          this.setKpi(
            'total',
            r.efetivos.totalRecords,
          );

          this.setKpi(
            'postos',
            r.postos.totalRecords,
          );

          this.setKpi(
            'ativos',
            r.efetivosAtivos.totalRecords,
          );

          this.setKpi(
            'inativos',
            r.efetivosInativos.totalRecords,
          );

          this.loadingKpis = false;

          this.cdr.detectChanges();
        },

        error: () => {
          this.loadingKpis = false;
          this.cdr.detectChanges();
        },
      });
  }

  // ───────────────────────────────────────────────────────
  // DROPDOWNS
  // ───────────────────────────────────────────────────────

  loadDropdowns(): void {
    forkJoin({
      funcoes: this.funcaoService.getAll({
        pageSize: 1000,
      }),

      categorias: this.categoriaService.list({
        pageSize: 100,
      }),

      postos: this.postoSvc.getAll({
        pageSize: 1000,
      }),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.funcoes =
            (res.funcoes.data || []).map((f: any) => ({
              value: f.id,
              label: f.designacao,
            }));

          this.categorias =
            res.categorias.data;

          this.generos = [
            {
              label: 'Masculino',
              value: GeneroEnum.MASCULINO,
            },
            {
              label: 'Feminino',
              value: GeneroEnum.FEMININO,
            },
          ];

          this.postos =
            res.postos.data || [];

          this.cdr.detectChanges();
        },

        error: (err) => {
          console.error(
            'Não foi possível carregar os dados dos dropdowns.',
            err,
          );
        },
      });
  }

  // ───────────────────────────────────────────────────────
  // KPI HELPER
  // ───────────────────────────────────────────────────────

  private setKpi(
    key: string,
    value: number,
  ): void {
    const item = this.indicators.find(
      (i) => i.key === key,
    );

    if (item) {
      item.value = String(value);
    }
  }

  // ───────────────────────────────────────────────────────
  // LISTA DE EFETIVOS
  // ───────────────────────────────────────────────────────

  loadEfetivos(
    queryParams: Record<string, any> = {},
  ): void {
    this.skeletonLoading = true;

    const params: Record<string, any> = {
      pageNumber:
        queryParams['pageNumber'] ?? 1,

      pageSize:
        queryParams['pageSize'] ?? 10,

      OrderBy:
        queryParams['OrderBy'] ?? 'id desc',

      ...queryParams,
    };

    if (
      this.activeTab !== 'todos' &&
      params['EstadoEfetivo'] === undefined
    ) {
      params['EstadoEfetivo'] =
        this.activeTab;
    }

    if (
      params['EstadoEfetivo'] !== undefined &&
      params['EstadoEfetivo'] !== null
    ) {
      const stateMap: Record<string, number> = {
        Ativo: EstadoEfetivoEnum.ATIVO,
        Inativo: EstadoEfetivoEnum.INATIVO,
        Afastado: EstadoEfetivoEnum.AFASTADO,
        Suspensos: EstadoEfetivoEnum.SUSPENSO,
        De_Folga: EstadoEfetivoEnum.DE_FOLGA,
        Ferias: EstadoEfetivoEnum.FERIAS,
      };

      if (
        typeof params['EstadoEfetivo'] ===
          'string' &&
        stateMap[
          params['EstadoEfetivo']
        ] !== undefined
      ) {
        params['EstadoEfetivo'] =
          stateMap[
            params['EstadoEfetivo']
          ];
      }
    }

    this.efetivoSvc
      .getAll(params)
      .pipe(
        takeUntilDestroyed(
          this.destroyRef,
        ),
      )
      .subscribe({
        next: (res) => {
          const rawData =
            res.data || [];

          const data = rawData.map(
            (e: any) => ({
              ...e,
              codigoMec: e.codigoMec || '',
              mac: e.mac || e.Mac || '',
              Mac: e.Mac || e.mac || '',
              registroId: e.id
                ? 'ID: EF-' +
                  String(e.id).padStart(
                    3,
                    '0',
                  )
                : e.numBI || '',
            }),
          );

          this.pagination =
            new Pagination<any>().deserialize(
              {
                ...res,
                data,
              },
            );

          this.totalCount =
            res.totalRecords ??
            data.length;

          this.fotoPreviewMap = {};

          data.forEach((e: any) => {
            const img =
              e.urlFotografia ||
              e.foto;

            if (
              e.id != null &&
              img
            ) {
              const versao =
                e.dataAtualizacao ??
                '';

              const separator =
                img.includes('?')
                  ? '&'
                  : '?';

              this.fotoPreviewMap[
                Number(e.id)
              ] = versao
                ? `${img}${separator}v=${encodeURIComponent(
                    versao,
                  )}`
                : img;
            }
          });

          this.setKpi(
            'total',
            this.totalCount,
          );

          this.skeletonLoading = false;

          this.cdr.detectChanges();
        },

        error: () => {
          this.skeletonLoading = false;
          this.cdr.detectChanges();
        },
      });
  }

  // ───────────────────────────────────────────────────────
  // TABS
  // ───────────────────────────────────────────────────────

  setActiveTab(tabId: TabType): void {
    this.activeTab = tabId;

    if (tabId === 'todos') {
      this.loadEfetivos();
      return;
    }

    const stateMap: Record<string, number> = {
      Ativo: EstadoEfetivoEnum.ATIVO,
      Inativo: EstadoEfetivoEnum.INATIVO,
      Afastado: EstadoEfetivoEnum.AFASTADO,
      Suspensos: EstadoEfetivoEnum.SUSPENSO,
      De_Folga: EstadoEfetivoEnum.DE_FOLGA,
      Ferias: EstadoEfetivoEnum.FERIAS,
    };

    const numState =
      stateMap[tabId];

    this.loadEfetivos({
      EstadoEfetivo: numState,
    });
  }

  // ───────────────────────────────────────────────────────
  // FILTROS
  // ───────────────────────────────────────────────────────

  limparFiltros(): void {
    this.filtrosForm.reset();
  }

  aplicarFiltrosDrawer(): void {
    const val =
      this.filtrosForm.value;

    const params: Record<string, any> =
      {};

    Object.entries(val).forEach(
      ([k, v]) => {
        if (
          v !== null &&
          v !== undefined &&
          v !== ''
        ) {
          params[k] = v;
        }
      },
    );

    this.filtrosPainelAberto = false;

    this.aplicarFiltros(params);
  }

  aplicarFiltros(
    filtros: any,
  ): void {
    this.loadEfetivos({
      pageNumber: 1,
      ...filtros,
    });
  }

  onPageChange(
    event: any,
  ): void {
    this.loadEfetivos({
      pageNumber:
        event.page + 1,
      pageSize:
        event.rows,
    });
  }

  onSearchInput(
    event: Event,
  ): void {
    const value =
      (
        event.target as
          HTMLInputElement
      ).value;

    this.filtrosForm.patchValue({
      nome: value,
    });

    this.loadEfetivos({
      ...this.filtrosForm.value,
      pageNumber: 1,
    });
  }

  onPostoFilterChange(
    event: any,
  ): void {
    const value =
      event.value;

    if (value) {
      this.loadEfetivos({
        ...this.filtrosForm.value,
        IdPosto: value,
        pageNumber: 1,
      });
    } else {
      this.loadEfetivos({
        ...this.filtrosForm.value,
        IdPosto: null,
        pageNumber: 1,
      });
    }
  }

  // ───────────────────────────────────────────────────────
  // DELETE
  // ───────────────────────────────────────────────────────

  confirmarDelete(
    efetivo: any,
  ): void {
    const id =
      efetivo?.id;

    if (!id) return;

    this.confirmSvc.confirm({
      header: 'Eliminar Efetivo',

      message: `O efetivo "${efetivo?.nome}" será eliminado. Deseja continuar?`,

      icon: 'pi pi-exclamation-triangle',

      acceptLabel: 'Eliminar',

      rejectLabel: 'Cancelar',

      acceptButtonStyleClass:
        'p-button-danger',

      accept: () => {
        this.efetivoSvc
          .delete(id)
          .pipe(
            takeUntilDestroyed(
              this.destroyRef,
            ),
          )
          .subscribe({
            next: () => {
              this.msgService.add({
                severity:
                  'success',

                summary:
                  'Eliminado',

                detail:
                  'Efetivo eliminado.',

                life: 3000,
              });

              this.loadEfetivos();
              this.estatisticasEfetivos();
            },
          });
      },
    });
  }

  // ───────────────────────────────────────────────────────
  // DESATIVAR
  // ───────────────────────────────────────────────────────

  toggleEstado(efetivo: any): void {
    const id = efetivo?.id;
    if (!id) return;

    this.confirmSvc.confirm({
      header: 'Desativar Efetivo',
      message: `O efetivo "${efetivo?.nome}" será desativado e deixará de poder ser escalado. Deseja continuar?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Desativar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.efetivoSvc
          .toggleStatus(id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.msgService.add({
                severity: 'success',
                summary: 'Sucesso',
                detail: 'Efetivo desativado com sucesso.',
                life: 3000,
              });
              this.loadEfetivos();
              this.estatisticasEfetivos();
            },
          });
      },
    });
  }

  reativar(efetivo: any): void {
    const id = efetivo?.id;
    if (!id) return;

    this.confirmSvc.confirm({
      header: 'Reativar Efetivo',
      message: `O efetivo "${efetivo?.nome}" será reativado e poderá voltar a ser escalado. Deseja continuar?`,
      icon: 'pi pi-check-circle',
      acceptLabel: 'Reativar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-success',
      accept: () => {
        this.efetivoSvc
          .reativar(id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.msgService.add({
                severity: 'success',
                summary: 'Sucesso',
                detail: 'Efetivo reativado com sucesso.',
                life: 3000,
              });
              this.loadEfetivos();
              this.estatisticasEfetivos();
            },
          });
      },
    });
  }

  abrirAcaoDrawer(
    efetivo: any,
    action: 'suspender' | 'afastar',
  ): void {
    if (!efetivo?.id) return;
    this.fichaEfetivoId = efetivo.id;
    this.fichaPendingAction = action;
    this.fichaDrawerVisible = true;
  }

  onFichaStatusChanged(): void {
    this.fichaPendingAction = null;
    this.loadEfetivos();
  }

  // ───────────────────────────────────────────────────────
  // DRAWERS
  // ───────────────────────────────────────────────────────

  criarEfetivo(): void {
    this.mostrarDrawer = true;
  }

  verDetalhe(
    efetivo: any,
  ): void {
    if (efetivo?.id) {
      this.fichaEfetivoId =
        efetivo.id;

      this.fichaDrawerVisible =
        true;
    }
  }

  editar(
    efetivo: any,
  ): void {
    if (efetivo?.id) {
      this.efetivoIdParaEditar =
        efetivo.id;

      if (
        this.fichaDrawerVisible
      ) {
        this.reabrirFichaAposEditar =
          true;

        this.fichaDrawerVisible =
          false;
      }

      this.mostrarDrawerEditar =
        true;
    }
  }

  onEditarDrawerVisibleChange(
    visible: boolean,
  ): void {
    this.mostrarDrawerEditar =
      visible;

    if (
      !visible &&
      this.reabrirFichaAposEditar
    ) {
      this.reabrirFichaAposEditar =
        false;

      setTimeout(() => {
        this.fichaDrawerVisible =
          true;

        this.cdr.detectChanges();
      }, 300);
    }
  }

  // ───────────────────────────────────────────────────────
  // ENUM
  // ───────────────────────────────────────────────────────

  private mapEnumToOptions(
    data: Record<
      string,
      string
    >,
  ) {
    return Object.entries(
      data || {},
    ).map(
      ([key, value]) => ({
        value: Number(key),
        label: value,
      }),
    );
  }
}